import { useState, useEffect, useCallback } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { Loader2, AlertCircle, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CGT_FORM_LABELS } from '@/types/cgt'
import type { FormDefinition, FormSubmission, CgtFormType } from '@/types/cgt'
import { formsApi } from '@/api/formsApi'
import { FormRenderer } from '@/components/renderer/FormRenderer'
import { useTenant } from '@/providers/TenantProvider'
import { useCurrentCgtUser } from '@/hooks/useCurrentCgtUser'
import { PageHeading } from '@/components/layout/PageHeading'
import { sortParam } from '@/lib/tableSort'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]


function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-blue-100 text-blue-800 border-blue-200',
    pending_review: 'bg-amber-100 text-amber-800 border-amber-200',
    completed: 'bg-green-100 text-green-800 border-green-200',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
  }
  const labels: Record<string, string> = {
    active: 'In progress',
    pending_review: 'Pending review',
    completed: 'Completed',
    cancelled: 'Cancelled',
  }
  return (
    <Badge className={`text-xs border ${styles[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {labels[status] ?? status}
    </Badge>
  )
}

// Per-user column layout. Patient MRN is locked (it identifies the row) and so
// is the View/Edit cell, which is the only way into a submission. See
// src/components/table/index.ts for the pattern.
const SUBMISSIONS_TABLE_ID = 'all-submissions'

// `sortable` columns sort SERVER-side (z8nrz7e4h6): this list is paged, so the
// ids here are the backend's allowed sort keys (SUBMISSION_SORTS in
// form-submissions.service).
const SUBMISSIONS_COLUMNS: TableColumnDef[] = [
  { id: 'patientMrn', header: 'Patient MRN', locked: true, defaultWidth: 180, sortable: true },
  { id: 'form', header: 'Form', defaultWidth: 200, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 140, sortable: true },
  { id: 'created', header: 'Created', defaultWidth: 130, sortable: true },
  { id: 'updated', header: 'Last updated', defaultWidth: 130, sortable: true },
  { id: 'open', header: 'Open', locked: true },
]

// The header classes this table already used, kept per column so it renders
// exactly as before.
const SUBMISSION_HEAD_CLASSES: Record<string, string> = {
  patientMrn: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  form: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  status: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  created: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  updated: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  open: 'px-4 py-3',
}

export function AllSubmissionsPage() {
  const currentUser = useCurrentCgtUser()
  const cols = useTableColumns(SUBMISSIONS_TABLE_ID, SUBMISSIONS_COLUMNS)
  const { selectedTenant } = useTenant()
  const [submissions, setSubmissions] = useState<FormSubmission[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mrnFilter, setMrnFilter] = useState('')
  const [debouncedMrn, setDebouncedMrn] = useState('')
  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(20)
  const [openedForm, setOpenedForm] = useState<{
    submission: FormSubmission
    definition: FormDefinition
  } | null>(null)
  const [openingId, setOpeningId] = useState<string | null>(null)

  // Debounce the MRN filter
  useEffect(() => {
    // Guard prevents the mount-time timer from resetting pagination 300ms in
    // (same pattern as PatientsOverviewPage).
    if (mrnFilter === debouncedMrn) return
    const id = setTimeout(() => { setDebouncedMrn(mrnFilter); setSkip(0) }, 300)
    return () => clearTimeout(id)
  }, [mrnFilter, debouncedMrn])

  useEffect(() => { setSkip(0) }, [take])

  // A new sort is a new ordering of the whole list, so page 1 again (z8nrz7e4h6).
  const sort = sortParam(cols.sort)
  const [sortOfPage, setSortOfPage] = useState(sort)
  if (sortOfPage !== sort) {
    setSortOfPage(sort)
    setSkip(0)
  }

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    formsApi
      .listAllSubmissions({ q: debouncedMrn, skip, take, sort })
      .then(({ items, total: t }) => {
        setSubmissions(items)
        setTotal(t)
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load submissions.'))
      .finally(() => setLoading(false))
  }, [debouncedMrn, skip, take, sort])

  useEffect(() => { load() }, [load])

  const handleOpen = useCallback(async (sub: FormSubmission) => {
    setOpeningId(sub.id)
    try {
      const def = await formsApi.getDefinition(sub.formDefinitionId)
      setOpenedForm({ submission: sub, definition: def })
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Could not open form.')
    } finally {
      setOpeningId(null)
    }
  }, [])

  const handleSubmissionUpdated = useCallback((updated: FormSubmission) => {
    setSubmissions(prev => prev.map(s => (s.id === updated.id ? updated : s)))
    setOpenedForm(prev => prev ? { ...prev, submission: updated } : prev)
  }, [])

  return (
    <>
      <div className='container py-8'>
        <div className='flex items-center justify-between mb-6'>
          <PageHeading title='All Submissions' description='Every form submission across all patients. Filter by MRN to narrow results.' />
          <Button variant='outline' size='sm' onClick={load} disabled={loading}>
            Refresh
          </Button>
        </div>

        {/* MRN filter */}
        <div className='mb-4 flex items-center justify-between gap-2'>
          <div className='relative max-w-xs flex-1'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <Input
              className='pl-9'
              placeholder='Filter by patient MRN…'
              value={mrnFilter}
              onChange={e => setMrnFilter(e.target.value)}
            />
          </div>
        </div>

        {loading && (
          <div className='flex items-center justify-center py-20'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {error && (
          <div className='flex items-center gap-2 text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 mb-4'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {error}
          </div>
        )}

        {!loading && total === 0 && (
          <div className='text-center py-16 text-muted-foreground'>
            <p className='text-sm'>
              {debouncedMrn ? 'No submissions match that MRN.' : 'No submissions yet.'}
            </p>
          </div>
        )}

        {total > 0 && (
          <>
          <div className='border rounded-lg overflow-hidden'>
            <table className='w-full text-sm'>
              <thead className='bg-table-header border-b'>
                <tr>
                  {cols.visible.map(({ def }) => (
                    <ResizableTh
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                      className={SUBMISSION_HEAD_CLASSES[def.id]}
                      resizable={def.id !== 'open'}
                    >
                      {def.id === 'open' ? null : def.header}
                    </ResizableTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub, i) => {
                  const formLabel =
                    CGT_FORM_LABELS[sub.formType as CgtFormType] ?? sub.formType
                  return (
                    <tr
                      key={sub.id}
                      className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                    >
                      <ColumnCells
                        columns={cols}
                        cells={{
                          patientMrn: <td className='px-4 py-3 font-mono font-medium'>{sub.patientMrn}</td>,
                          form: <td className='px-4 py-3'>{formLabel}</td>,
                          status: (
                            <td className='px-4 py-3'>
                              <StatusBadge status={sub.status} />
                            </td>
                          ),
                          created: (
                            <td className='px-4 py-3 text-xs text-muted-foreground'>
                              {new Date(sub.createdAt).toLocaleDateString()}
                            </td>
                          ),
                          updated: (
                            <td className='px-4 py-3 text-xs text-muted-foreground'>
                              {new Date(sub.updatedAt).toLocaleDateString()}
                            </td>
                          ),
                          open: (
                            <td className='px-4 py-3 text-right'>
                              <Button
                                size='sm'
                                variant='outline'
                                disabled={openingId === sub.id}
                                onClick={() => handleOpen(sub)}
                              >
                                {openingId === sub.id ? (
                                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                                ) : (
                                  sub.status === 'completed' || sub.status === 'pending_review' ? 'View' : 'Edit'
                                )}
                              </Button>
                            </td>
                          ),
                        }}
                      />
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div className='flex items-center justify-between border-t bg-muted/20 px-4 py-2 text-xs text-muted-foreground'>
              <span>
                {total > 0
                  ? `${skip + 1}–${Math.min(skip + take, total)} of ${total} submissions`
                  : '0 submissions'}
              </span>
              <div className='flex items-center gap-2'>
                <select
                  value={take}
                  onChange={e => { setTake(Number(e.target.value)); setSkip(0) }}
                  className='rounded border bg-background px-2 py-0.5 text-xs'
                >
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <div className='flex items-center gap-1'>
                  <button
                    className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                    disabled={skip === 0}
                    onClick={() => setSkip(s => Math.max(0, s - take))}
                  >
                    <ChevronLeft className='h-3.5 w-3.5' />
                    Previous
                  </button>
                  <button
                    className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                    disabled={skip + take >= total}
                    onClick={() => setSkip(s => s + take)}
                  >
                    Next
                    <ChevronRight className='h-3.5 w-3.5' />
                  </button>
                </div>
              </div>
            </div>
          </div>
          </>
        )}
      </div>

      {/* Inline form editor */}
      <Dialog open={!!openedForm} onOpenChange={open => !open && setOpenedForm(null)}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>
              {openedForm
                ? CGT_FORM_LABELS[openedForm.definition.formType as CgtFormType] ??
                  openedForm.definition.name
                : ''}
            </DialogTitle>
          </DialogHeader>
          {openedForm && (
            <FormRenderer
              definition={openedForm.definition}
              submission={openedForm.submission}
              currentUser={currentUser}
              onSubmissionUpdated={handleSubmissionUpdated}
              tenantTimezone={selectedTenant?.timezone}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
